import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, FormControl, InputGroup, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FirebaseApp from "../firebase";
import { get, ref } from "firebase/database";
import { BsArrowLeft } from "react-icons/bs";

const formatTimestamp = (value) => {
  if (!value) return "-";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "-";
  const milliseconds = numericValue > 1_000_000_000_000 ? numericValue : numericValue * 1000;
  return new Date(milliseconds).toLocaleString();
};

const formatSelected = (selected) => {
  if (Array.isArray(selected)) {
    return selected.length ? selected.join(", ") : "-";
  }
  if (selected) {
    return String(selected);
  }
  return "-";
};

const renderQuestionnaire = (questionnaires) => {
  if (!questionnaires || typeof questionnaires !== "object") {
    return <p className="text-muted small mb-0">No questionnaire data.</p>;
  }

  const entries = Object.entries(questionnaires);
  if (!entries.length) {
    return <p className="text-muted small mb-0">No questionnaire data.</p>;
  }

  return entries.map(([key, question]) => (
    <div key={`${key}-${question?.text || "question"}`} className="mb-3">
      {
        question.text &&
        <>
          <div className="fw-bold small text-dark">Question: {question?.text || key}</div>
          <div className="small text-muted">
            <strong>Answer:</strong> {formatSelected(question?.selected)}
          </div>
        </>
      }
    </div>
  ));
};

const hasQuestionnaires = (stageData) => {
  if (!stageData || !stageData.questionnaires) {
    return false;
  }

  if (typeof stageData.questionnaires === "string") {
    return stageData.questionnaires.trim().length > 0;
  }

  if (typeof stageData.questionnaires === "object") {
    return !!Object.keys(stageData.questionnaires).length;
  }

  return false;
};

export const ResearchResults = () => {
  const { company_key } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [modalInfo, setModalInfo] = useState({
    show: false,
    stageLabel: "",
    stageData: null,
    researchType: "",
    deviceId: "",
    userIdentifier: "",
  });
  const [deviceUsers, setDeviceUsers] = useState({});

  const handleViewStage = (stageLabel, stageData, researchType, deviceId, userIdentifier) => {
    setModalInfo({
      show: true,
      stageLabel,
      stageData,
      researchType,
      deviceId,
      userIdentifier,
    });
  };

  const exportResearchResults = () => {
    const rows = [];

    results.forEach((result) => {
      result.devices.forEach((device) => {
        [
          { label: "Pre-session", data: device.pre },
          { label: "Post-session", data: device.post },
        ].forEach(({ label, data }) => {
          if (!data || !hasQuestionnaires(data)) {
            return;
          }

          const questionnaires = typeof data.questionnaires === "object" ? data.questionnaires : {};

          Object.entries(questionnaires).forEach(([key, question]) => {
            if (question.text) {
              rows.push({
                researchType: result.researchType,
                userIdentifier: deviceUsers[device.deviceId] || "-",
                deviceId: device.deviceId,
                stage: label,
                language: data?.meta?.language || "",
                completedAt: formatTimestamp(data?.meta?.completed_at),
                question: question?.text || key,
                answer: formatSelected(question?.selected),
              });
            }
          });
        });
      });
    });

    if (!rows.length) {
      toast.info("No research responses available to export.");
      return;
    }

    const quote = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = ["Research Type", "User Identifier", "Device ID", "Stage", "Language", "Completed At", "Question", "Answer"];
    const csvRows = [header, ...rows.map((row) => [
      row.researchType,
      row.userIdentifier,
      row.deviceId,
      row.stage,
      row.language,
      row.completedAt,
      row.question,
      row.answer,
    ])];
    const csvContent = csvRows.map((row) => row.map(quote).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${company_key}_research_results.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const closeModal = () => {
    setModalInfo((prev) => ({ ...prev, show: false, userIdentifier: "" }));
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const db = await FirebaseApp();
        const [snapshot, deviceSnapshot] = await Promise.all([
          get(ref(db, "ResearchResult")),
          get(ref(db, `UsedCompanyKeys/${company_key}`)),
        ]);
        const data = snapshot.exists() ? snapshot.val() : {};
        const parsed = [];
        const deviceMap = {};

        if (deviceSnapshot.exists()) {
          deviceSnapshot.forEach((childSnapshot) => {
            const payload = childSnapshot.val() || {};
            const identifier =
              payload.company_user_identifier ||
              payload.user_identifier ||
              payload.company_user_id ||
              payload.user_id ||
              "";
            if (identifier) {
              deviceMap[childSnapshot.key] = identifier;
            } else {
              deviceMap[childSnapshot.key] = "-";
            }
          });
        }

        Object.entries(data).forEach(([researchType, researchPayload]) => {
          if (!researchPayload) return;
          const companyPayload = researchPayload[company_key];
          if (!companyPayload) return;

          const devices = Object.entries(companyPayload).map(([deviceId, deviceData]) => ({
            deviceId,
            pre: deviceData?.pre,
            post: deviceData?.post,
          }));

          if (devices.length) {
            parsed.push({ researchType, devices });
          }
        });

        parsed.sort((a, b) => a.researchType.localeCompare(b.researchType));
        setResults(parsed);
        setDeviceUsers(deviceMap);
      } catch (err) {
        console.error("Error fetching research results:", err);
        toast.error("Unable to load research results.");
      } finally {
        setLoading(false);
      }
    };

    if (company_key) {
      setDeviceUsers({});
      fetchResults();
    }
  }, [company_key]);

  const visibleResults = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return results
      .map((result) => {
        const devices = result.devices.filter((device) => {
          if (!normalizedSearch) {
            return true;
          }
          return device.deviceId?.toLowerCase().includes(normalizedSearch);
        });

        return { ...result, devices };
      })
      .filter((result) => result.devices.length > 0);
  }, [results, search]);

  const noResultsMessage = search.trim()
    ? "No devices match that search."
    : "No research results found for this company.";

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <Row className="align-items-center mb-3">
          <Col>
            <h1>
              <button
                type="button"
                className="back-link-icon"
                aria-label="Go back"
                onClick={() => navigate(-1)}
              >
                <BsArrowLeft aria-hidden="true" />
              </button>
              <span className="text-orange">{company_key}</span> Research Results
            </h1>
          </Col>
        </Row>
        <Row className="d-flex justify-content-between">
          <Col md={4}>
            <div className="text-left mt-3">
              <InputGroup>
                <FormControl
                  className="search-field"
                  placeholder="Search by Device ID"
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </div>
          </Col>
          <Col md="auto">
            <div className="text-end mt-3">
              <Button className="export-button" onClick={exportResearchResults}>
                Export to CSV
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <section className="section dashboard">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : !visibleResults.length ? (
          <p className="text-center text-muted mb-0">{noResultsMessage}</p>
        ) : (
          visibleResults.map((result) => (
            <div key={result.researchType} className="mb-5">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap">
                <div>
                  <h5 className="mb-0">{result.researchType.toUpperCase()}</h5>
                  <small className="text-muted">
                    {result.devices.length} device{result.devices.length > 1 ? "s" : ""}
                  </small>
                </div>
              </div>
              <Table striped bordered hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>User Identifier</th>
                    <th>Device ID</th>
                    <th>View pre questions</th>
                    <th>View post questions</th>
                  </tr>
                </thead>
                <tbody>
                  {result.devices.map((device) => (
                    <tr key={`${result.researchType}-${device.deviceId}`}>
                      <td>{deviceUsers[device.deviceId] || "-"}</td>
                      <td>{device.deviceId}</td>
                      <td>
                        <Button
                          variant={hasQuestionnaires(device.pre) ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          disabled={!hasQuestionnaires(device.pre)}
                          onClick={() =>
                            handleViewStage(
                              "Pre-session",
                              device.pre,
                              result.researchType,
                              device.deviceId,
                              deviceUsers[device.deviceId] || "-"
                            )
                          }
                        >
                          View pre questions
                        </Button>
                      </td>
                      <td>
                        <Button
                          variant={hasQuestionnaires(device.post) ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          disabled={!hasQuestionnaires(device.post)}
                          onClick={() =>
                            handleViewStage(
                              "Post-session",
                              device.post,
                              result.researchType,
                              device.deviceId,
                              deviceUsers[device.deviceId] || "-"
                            )
                          }
                        >
                          View post questions
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))
        )}
      </section>

      <Modal show={modalInfo.show} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalInfo.stageLabel} responses</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-bold mb-1 small text-muted">
            Research: {modalInfo.researchType?.toUpperCase() || "-"}
          </p>
          <p className="fw-bold mb-1 small text-muted">
            User Identifier: {modalInfo.userIdentifier || "-"}
          </p>
          <p className="fw-bold mb-3 small text-muted">
            Device ID: {modalInfo.deviceId || "-"}
          </p>
          {modalInfo.stageData ? (
            <>
              <p className="text-muted small mb-3">
                <strong>Language:</strong> {modalInfo.stageData?.meta?.language || "-"}{" "}
                <strong className="ms-3">Completed at:</strong>{" "}
                {formatTimestamp(modalInfo.stageData?.meta?.completed_at)}
              </p>
              {renderQuestionnaire(modalInfo.stageData.questionnaires)}
            </>
          ) : (
            <p className="text-muted small mb-0">No data available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};
